import CircularLoader from 'components/CircularLoader.tsx';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { useAdornmentGetters } from 'components/forms/inputs/rhf-inputs/useAdornmentGetters.ts';
import { usePaginatedPopup } from 'components/forms/inputs/rhf-inputs/usePaginatedList.ts';
import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from 'components/ui/combobox';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll.ts';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { groupConsecutiveOptions, OptionGroup } from 'utils/autocompleteGrouping';
import { t } from 'utils/getTranslations.ts';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { getSelectorKey } from '../../getSelectorKey.ts';
import {
    DynamicDropdownFilterForm,
    MainFilterForm,
    MainFilterInForm,
    QuickFilterForm,
    SelectOption,
} from '../../ListTableTypes.tsx';
import { FilterLabel } from './FilterLabel';

const getOptionLabel = (option: SelectOption) => option.label.toString();

export const DynamicDropdown: React.FC<{
    filter: DynamicDropdownFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    mainFiltersInForm: MainFilterInForm[];
    onChange: (value: SelectOption | null, groupSelector: string) => void;
}> = React.memo(({ filter, onChange, quickFilters, mainFilters, mainFiltersInForm }) => {
    const { paginated } = filter;
    const [fetchData, { data: fetchedData }] = paginated.fetch();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const lastNameFirst = useLastNameFirst();
    const fallbackTranslation = t('field.other');
    const mappedFetchedSelectedOption = useMemo(() => {
        const data = fetchedData?.content?.at(0) || undefined;
        return data
            ? {
                  value: get(data, paginated.getOptionIdentifier),
                  label: optionDisplayLabel(data, lastNameFirst, get(data, paginated.getOptionLabel)),
              }
            : undefined;
    }, [fetchedData, paginated.getOptionIdentifier, paginated.getOptionLabel, lastNameFirst]);
    const {
        dataToDisplay,
        handleLoadMore,
        handleSearchChange,
        hasNextPage,
        isLoading,
        isError,
        isOpen,
        isOpenPending,
        actionsRef,
        setOpen,
        dropPendingOpen,
        onInputKeyDown,
    } = usePaginatedPopup<Record<string, unknown>>({
        ...paginated,
        quickFilters,
        mainFilters: mainFiltersInForm,
    });
    const mappedOptions = useMemo<SelectOption[]>(() => {
        return dataToDisplay.map((data) => ({
            ...data,
            value: get(data, paginated.getOptionIdentifier),
            label: optionDisplayLabel(data, lastNameFirst, get(data, paginated.getOptionLabel)),
        }));
    }, [dataToDisplay, paginated.getOptionIdentifier, paginated.getOptionLabel, lastNameFirst]);

    const { getEndAdornment } = useAdornmentGetters(filter.getOptionStartAdornment, filter.getOptionEndAdornment);
    const getSameNameEmail = useSameNameEmail(mappedOptions);

    const listRootWrapperRef = useRef<HTMLDivElement | null>(null);
    const [loaderEl, setLoaderEl] = useState<HTMLDivElement | null>(null);
    useInfiniteScroll(
        loaderEl,
        {
            observer: { root: listRootWrapperRef.current, rootMargin: '75px', threshold: 0 },
            disabled: isError,
            isLoading,
        },
        handleLoadMore
    );

    const fetchedDataExists =
        filter.selected && fetchedData?.content.at(0)?.id == filter.selected.value ? undefined : filter.selected?.value;

    const unfetchedValue = Boolean(isInitialLoad && fetchedDataExists);

    useEffect(() => {
        setIsInitialLoad(false);
    }, []);

    useEffect(() => {
        if (
            !unfetchedValue ||
            filter.selected?.value === undefined ||
            filter.selected?.value === null ||
            isEqual(filter.selected, mappedFetchedSelectedOption)
        ) {
            return;
        }
        fetchData({
            filters: [paginated.filters, `id==${filter.selected.value}`].filter(Boolean).join(';'),
            page: 0,
            pageSize: 1,
        })
            .then((val) => val.data?.content.at(0) || undefined)
            .then((data) =>
                data !== undefined
                    ? {
                          value: get(data, paginated.getOptionIdentifier),
                          label: optionDisplayLabel(data, lastNameFirst, get(data, paginated.getOptionLabel)),
                      }
                    : undefined
            )
            .then((data) => {
                if (!data || isEqual(filter.selected, data)) {
                    return;
                }
                onChange(data, getSelectorKey(filter));
            });
    }, [
        unfetchedValue,
        fetchData,
        filter.selected,
        paginated.filters,
        onChange,
        filter,
        mappedFetchedSelectedOption,
        paginated.getOptionIdentifier,
        paginated.getOptionLabel,
        lastNameFirst,
    ]);

    const getGroupedBy = (option: SelectOption) => {
        if (!option) {
            return fallbackTranslation;
        }

        if (typeof filter.paginated.groupByField === 'string') {
            return get(option, filter.paginated.groupByField);
        }

        return fallbackTranslation;
    };

    const isGrouped = !!filter.paginated.groupByField;
    // The loaded page is already ordered by the group field, so the groups come out in the order the
    // rows arrive.
    const groupedOptions = useMemo<OptionGroup<SelectOption>[]>(
        () => (isGrouped ? groupConsecutiveOptions(mappedOptions, (option) => String(getGroupedBy(option))) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mappedOptions, isGrouped, filter.paginated.groupByField, fallbackTranslation]
    );

    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && filter.selected != null) {
            onChange(null, getSelectorKey(filter));
        }
    }, [filter, isHidden, onChange]);

    if (isHidden) {
        return null;
    }

    const renderOptionRow = (option: SelectOption) => (
        <ComboboxItem key={String(option.value)} value={option}>
            <span className="flex w-full min-w-0 items-center">
                <span className="min-w-0 flex-1 truncate">
                    <PersonOptionLabel option={option} label={option.label} sameNameEmail={getSameNameEmail(option)} />
                </span>
                {filter.getOptionEndAdornment ? (
                    <span className="ml-[.5ch] flex shrink-0 items-center">{getEndAdornment(option)}</span>
                ) : null}
            </span>
        </ComboboxItem>
    );

    return (
        <div className="flex grow flex-col gap-2" key={`filter-${filter.selector}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues} />
            )}
            <Combobox<SelectOption>
                items={(isGrouped ? groupedOptions : mappedOptions) as readonly SelectOption[]}
                value={unfetchedValue ? { value: '', label: '' } : (filter?.selected ?? null)}
                // Search runs on the server, so the popup shows exactly the page it was handed.
                filter={null}
                itemToStringLabel={getOptionLabel}
                isItemEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
                open={isOpen}
                onOpenChange={setOpen}
                actionsRef={actionsRef}
                onValueChange={(value) => onChange(value, getSelectorKey(filter))}
                onInputValueChange={(value, details) => {
                    // Only what the user typed drives a refetch; the input is also rewritten when an
                    // item is picked or the field loses focus.
                    if (details.reason === 'input-change' || details.reason === 'input-clear') {
                        handleSearchChange(value);
                    }
                }}
            >
                <ComboboxInput
                    id={`dynamic-dropdown-${getSelectorKey(filter)}`}
                    aria-label={filter.label}
                    className="bg-paper"
                    placeholder={t('search')}
                    showClear
                    aria-busy={isOpenPending || undefined}
                    onKeyDown={onInputKeyDown}
                    onBlur={dropPendingOpen}
                >
                    {isOpenPending ? (
                        <CircularLoader className="static mr-16 ml-auto group-has-[>[data-align=inline-end]>button]/input-group:mr-24 [&>span]:size-4" />
                    ) : null}
                </ComboboxInput>
                <ComboboxContent className="shadow-dropdown">
                    <ComboboxEmpty>{isLoading ? t('loading') : t('no-options')}</ComboboxEmpty>
                    {/* Mapped rather than handed to the list's function child: filtering is off here,
                        and the load-more row has to sit inside the scroll box. */}
                    <ComboboxList ref={listRootWrapperRef}>
                        {isGrouped
                            ? groupedOptions.map((group) => (
                                  <ComboboxGroup key={group.key} items={group.items}>
                                      <ComboboxLabel>{group.value}</ComboboxLabel>
                                      <ComboboxCollection>
                                          {(option: SelectOption) => renderOptionRow(option)}
                                      </ComboboxCollection>
                                  </ComboboxGroup>
                              ))
                            : mappedOptions.map((option) => renderOptionRow(option))}
                        {hasNextPage ? (
                            <div ref={setLoaderEl} className="relative py-2">
                                <CircularLoader className="static [&>span]:size-6" />
                            </div>
                        ) : null}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
        </div>
    );
});
